import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { SITE_URL, seoFor } from '../data/seo';

// ---------------------------------------------------------------------------
// Capul documentului, ținut la zi la fiecare navigare și la fiecare schimbare
// de limbă.
//
// Nu folosim o bibliotecă (react-helmet și rudele ei) pentru cinci etichete:
// aici e vorba de patru apeluri `setAttribute`, iar o dependență în plus ar
// costa mai mult decât rezolvă.
// ---------------------------------------------------------------------------

// Găsește eticheta sau o creează dacă lipsește — `index.html` nu le are pe
// toate, iar o pagină fără `og:title` propriu ar moșteni-o pe cea a gazdei.
function upsertMeta(attr, name, content) {
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export default function Seo() {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();

  const page = seoFor(pathname);
  const lang = i18n.language?.startsWith('en') ? 'en' : 'ro';

  useEffect(() => {
    const title = t(`${page.key}.title`);
    const description = t(`${page.key}.description`);
    const url = `${SITE_URL}${pathname === '/' ? '/' : pathname}`;

    document.title = title;

    // Cititoarele de ecran își aleg vocea și regulile de pronunție după acest
    // atribut; fără el, textul românesc era citit cu reguli englezești.
    document.documentElement.lang = lang;

    upsertMeta('name', 'description', description);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:locale', lang === 'en' ? 'en_US' : 'ro_RO');
    upsertLink('canonical', url);

    // Eticheta se scoate când pagina redevine indexabilă; altfel un `noindex`
    // pus pe /projects ar rămâne lipit de următoarea pagină vizitată.
    const robots = document.head.querySelector('meta[name="robots"]');
    if (page.noindex) {
      upsertMeta('name', 'robots', 'noindex, follow');
    } else if (robots) {
      robots.remove();
    }
  }, [page.key, page.noindex, pathname, lang, t]);

  return null;
}
